import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  OnInit,
  resource,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { formatDate } from '@angular/common';
import { RosterContent } from '@softapps/pms/roster-core';
import { SalesDocumentItemRepository } from '@softapps/wws/sales';
import { ArtifactDialogResult } from '../../types/artifact-dialog';
import { RosterDeliveryContent } from '../../types/roster-delivery-content';
import { DeliveryNotePlanMode } from '../../types/delivery-note-plan-type';
import {
  AccordionComponent,
  AccordionItemComponent,
  AccordionItemContentComponent,
  AccordionItemHeaderComponent,
  AccordionItemLeadingComponent,
  AccordionItemTitleComponent,
  BadgeComponent,
  BadgeVariant,
  ButtonComponent,
  FormatPipe,
  IconComponent,
  InputComponent,
  LoadingSpinnerComponent,
  MultilineInputComponent,
  ResolvableModal,
  TranslatePipe,
} from '@softline/ui-core';
import { DateInputComponent, TimeInputComponent } from '@softline/ui-core/date-input';
import { DsTabsComponent, TabItem } from '@softline/ui-core/tabs';
import { DeliveryNoteTabName } from '../../types/delivery-note-tab-name';
import { EinheitPipe } from '../../pipes/einheit.pipe';

@Component({
  selector: 'soft-event-artifact-dialog',
  imports: [
    ReactiveFormsModule,
    FormatPipe,
    EinheitPipe,
    BadgeComponent,
    ButtonComponent,
    DsTabsComponent,
    DateInputComponent,
    TimeInputComponent,
    InputComponent,
    MultilineInputComponent,
    IconComponent,
    TranslatePipe,
    AccordionComponent,
    AccordionItemComponent,
    AccordionItemHeaderComponent,
    AccordionItemLeadingComponent,
    AccordionItemTitleComponent,
    AccordionItemContentComponent,
    LoadingSpinnerComponent,
  ],
  providers: [SalesDocumentItemRepository], //needed for artikelliste as artikelliste is not part of the delivery-note-roster content response
  templateUrl: './event-artifact-dialog.component.html',
  styleUrl: './event-artifact-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class EventArtifactDialogComponent
  extends ResolvableModal<ArtifactDialogResult | 'CANCEL' | 'DELETE'>
  implements OnInit
{
  readonly event = input<RosterContent | undefined>(undefined);
  readonly resource = input<(string | number)[]>([]);
  readonly mode = input<DeliveryNotePlanMode | null>(null);
  readonly isReadonly = input<boolean>(false);
  readonly denyDelete = input<boolean>(false);
  readonly artifactTabTitle = input<string>('Artefakt');

  // --- artikelliste ---
  readonly #salesDocumentItemRepository = inject(SalesDocumentItemRepository);

  protected readonly artifact = computed(() => {
    const ev = this.event();
    return (ev as unknown as RosterDeliveryContent | undefined)?.additionalData
      ?.additionalDetail?.[0];
  });

  protected readonly salesDocumentId = computed(() => {
    const tabId = this.artifact()?.tabId;
    return tabId != null ? `SALES_ORDER_${tabId}` : null;
  });

  protected readonly itemsResource = resource({
    params: () => ({ id: this.salesDocumentId() }),
    loader: async ({ params }) => {
      if (!params.id) return [];
      return this.#salesDocumentItemRepository.load({ id: params.id });
    },
  });

  protected readonly items = computed(() =>
    this.itemsResource.hasValue() ? this.itemsResource.value() : []
  );
  protected readonly itemsError = computed(() => this.itemsResource.error());
  // ------

  protected readonly modeLabel = computed(() => {
    const mode = this.mode();
    if (!mode) return;
    if (mode === DeliveryNotePlanMode.Grob) return '#DN_ROSTER.DIALOG.GROB';
    if (mode === DeliveryNotePlanMode.Detail) return '#DN_ROSTER.DIALOG.DETAIL';
    return null;
  });

  protected readonly status = computed<{ label: string; color: BadgeVariant } | null>(
    () => {
      const artifact = this.artifact();
      if (!artifact || artifact.tabName === DeliveryNoteTabName.PurchaseOrder)
        return null;
      const value = artifact.data.find(
        (d) => d.label === 'Status Kommissionierung'
      )?.value;
      const status = value || '0';
      switch (status) {
        case '0':
          return { label: '#DN_ROSTER.STATUS.NOT_PICKED', color: 'danger' };
        case '1000':
          return { label: '#DN_ROSTER.STATUS.FULLY_PICKED', color: 'success' };
        default:
          return { label: '#DN_ROSTER.STATUS.PARTIALLY_PICKED', color: 'warning' };
      }
    }
  );

  readonly activeTab = model<'event' | 'artefakt'>('event');

  readonly mainTabs = computed<TabItem<'event' | 'artefakt'>[]>(() => [
    { title: 'Event', value: 'event' },
    { title: this.artifactTabTitle(), value: 'artefakt' },
  ]);

  form = new FormGroup({
    datum: new FormControl<string | null>(null),
    creatorUser: new FormControl<string | null>(null),
    createdAt: new FormControl<string | null>(null),
    changedAt: new FormControl<string | null>(null),
    beginTime: new FormControl<string | null>(null),
    endTime: new FormControl<string | null>(null),
    tourDestination: new FormControl<string | null>(null),
    tour: new FormControl<string | null>(null),
    comment: new FormControl<string | null>(null),
  });

  artefaktData: { label: string; value: string }[] = [];

  ngOnInit(): void {
    const ev = this.event();

    if (!ev) return;
    const ad = (ev as unknown as RosterDeliveryContent).additionalData ?? {};

    this.form = new FormGroup({
      datum: new FormControl<string | null>(ev.start?.toString().split('T')[0] ?? null),
      creatorUser: new FormControl<string | null>({
        value: ad.creatorUser || 'keine Angabe',
        disabled: true,
      }),
      createdAt: new FormControl<string | null>({
        value: ad.createdAt
          ? formatDate(ad.createdAt, 'dd.MM.yyyy HH:mm', 'de-AT')
          : null,
        disabled: true,
      }),
      changedAt: new FormControl<string | null>({
        value: ad.changedAt
          ? formatDate(ad.changedAt, 'dd.MM.yyyy HH:mm', 'de-AT')
          : null,
        disabled: true,
      }),
      beginTime: new FormControl<string | null>(
        ad.beginTime?.split('T')[1]?.substring(0, 5) ?? ad.beginTime ?? null
      ),
      endTime: new FormControl<string | null>(
        ad.endTime?.split('T')[1]?.substring(0, 5) ?? ad.endTime ?? null
      ),
      tourDestination: new FormControl<string | null>(ad.tourDestination ?? null),
      tour: new FormControl<string | null>(ad.tour ?? null),
      comment: new FormControl<string | null>(ad.comment ?? null),
    });

    const detail = this.artifact();
    this.artefaktData = [
      ...(detail?.key != null
        ? [{ label: 'Kunde', value: detail.key + ' - ' + detail.name }]
        : []),
      ...(detail?.data ?? []).map((d: { label: string; value: string }) => ({
        label: d.label,
        value: d.value,
      })),
    ];
  }

  save(): void {
    if (this.form.invalid) return;
    const v = this.form.getRawValue();
    const ev = this.event();
    const ad = (ev as unknown as RosterDeliveryContent)?.additionalData ?? {};

    const eventPayload: Partial<RosterContent> = {
      additionalData: {
        ...ad,
        datum: v.datum,
        beginTime: v.beginTime ?? ad.beginTime,
        endTime: v.endTime ?? ad.endTime,
        comment: v.comment ?? ad.comment,
        tour: v.tour ?? ad.tour,
      } as any,
    };

    this.resolve({ eventPayload });
  }

  cancel(): void {
    this.resolve('CANCEL');
  }

  delete(): void {
    this.resolve('DELETE');
  }
}
